import React, { useState } from 'react';
import { Form, Input, Button, message, Space, Typography, Divider, Select, Tag } from 'antd';
import { MobileOutlined, NumberOutlined, EditOutlined, TagOutlined } from '@ant-design/icons';
import { secureLogger } from '../../utils/secureLogger';
import { API_CONFIG } from '../../config/api';

const { Text } = Typography;

interface DeviceRegistrationProps {
  onSuccess: (device: any) => void;
  onCancel: () => void;
}

const DeviceRegistration: React.FC<DeviceRegistrationProps> = ({ onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: { imei: string; name: string; tags: string[] }) => {
    setLoading(true);
    try {
      const { fetchAuthSession } = await import('../../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      secureLogger.debug('Device registration session obtained');
      secureLogger.debug('Token exists for device registration', { hasToken: !!token });
      secureLogger.debug('Token length for device registration', { length: token?.length });
      
      if (!token) {
        message.error('認証トークンが見つかりません。再ログインしてください。');
        return;
      }
      
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DEVICES}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          device_id: values.imei,
          name: values.name,
          tags: values.tags || [],
        }),
      });

      if (response.ok) {
        const result = await response.json();
        message.success('デバイスを登録しました');
        onSuccess({
          id: result.device.id,
          device_id: result.device.device_id,
          name: result.device.name,
          tags: values.tags || [],
          status: result.device.status || 'offline',
          created_at: result.device.created_at || new Date().toISOString(),
          last_seen_at: result.device.last_seen_at
        });
      } else {
        const error = await response.json();
        secureLogger.error('Device registration failed', { status: response.status });
        secureLogger.error('Device registration error response', { status: response.status, hasDetail: !!error.detail });
        message.error(error.detail || 'デバイス登録に失敗しました');
      }
    } catch (error) {
      secureLogger.error('Device registration catch error', { error: 'Network or parsing error' });
      message.error('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ marginBottom: 24 }}>
        <Text type="secondary">
          THINKLETデバイスのIMEI番号と識別しやすい名前を入力してください。
        </Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <Form.Item
          label={
            <Space>
              <NumberOutlined />
              IMEI番号
            </Space>
          }
          name="imei"
          rules={[
            { required: true, message: 'IMEI番号を入力してください' },
            { 
              pattern: /^\d{15}$/, 
              message: 'IMEI番号は15桁の数字で入力してください' 
            },
          ]}
        >
          <Input
            placeholder="例: 123456789012345"
            maxLength={15}
            style={{ fontFamily: 'monospace' }}
          />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <EditOutlined />
              デバイス名
            </Space>
          }
          name="name"
          rules={[
            { required: true, message: 'デバイス名を入力してください' },
            { max: 50, message: 'デバイス名は50文字以内で入力してください' },
          ]}
        >
          <Input
            placeholder="例: メインデバイス、外出用THINKLET"
            maxLength={50}
          />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <TagOutlined />
              タグ
            </Space>
          }
          name="tags"
        >
          <Select
            mode="tags"
            placeholder="タグを選択または入力してください"
            options={[
              { label: 'メイン', value: 'メイン' },
              { label: '会議用', value: '会議用' },
              { label: '外出用', value: '外出用' },
              { label: 'テスト用', value: 'テスト用' },
              { label: '自宅用', value: '自宅用' },
              { label: 'オフィス用', value: 'オフィス用' },
            ]}
            tagRender={(props) => {
              const { label, closable, onClose } = props;
              return (
                <Tag
                  color="blue"
                  closable={closable}
                  onClose={onClose}
                  style={{ marginRight: 3 }}
                >
                  {label}
                </Tag>
              );
            }}
          />
        </Form.Item>

        <Divider />

        <Form.Item style={{ marginBottom: 0 }}>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<MobileOutlined />}
            >
              デバイスを登録
            </Button>
            <Button onClick={onCancel}>
              キャンセル
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default DeviceRegistration;